import React, { useState, useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    useMediaQuery,
    useTheme,
    Box,
    Typography,
    Divider,
    Chip,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Architecture as SitePlansIcon,
    AssignmentTurnedIn as DeficienciesIcon,
    Description as ReportsIcon,
    FileCopy as CloseoutDocsIcon,
    Settings as SettingsIcon,
    BarChart as UsageDashboardIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Home as HomeIcon,
    Work as ProjectsIcon,
    Logout as LogoutIcon,
} from '@mui/icons-material';
import cookie from 'js-cookie';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../utils/permissions';

const API_URL = process.env.REACT_APP_API_URL;

// Design tokens — single source of truth for sidebar visual style
const T = {
    bg: `linear-gradient(rgba(11, 21, 55, 0.75), rgba(11, 21, 55, 0.75)), url('/Moonlight-Connect-login-background.svg') center / cover no-repeat #0B1537`,
    borderRight: '1px solid rgba(255, 255, 255, 0.06)',
    accent: '#4A9EFF',
    activeBg: 'rgba(255, 255, 255, 0.07)',
    hoverBg: 'rgba(255, 255, 255, 0.05)',
    activeText: '#FFFFFF',
    inactiveText: 'rgba(255, 255, 255, 0.65)',
    inactiveIcon: 'rgba(255, 255, 255, 0.75)',
    sectionLabel: 'rgba(255, 255, 255, 0.45)',
    divider: 'rgba(255, 255, 255, 0.08)',
    transition: 'background-color 160ms ease, border-color 160ms ease',
};

/**
 * Styled version of MUI's Drawer component.
 * Maintains consistent width regardless of content.
 */
const StyledDrawer = styled(Drawer, {
    shouldForwardProp: (prop) => prop !== 'mini',
})(({ theme, mini }) => ({
    width: mini ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-width)',
    maxWidth: 'var(--sidebar-max-width)',
    flexShrink: 0,
    '& .MuiDrawer-paper': {
        width: mini ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-width)',
        maxWidth: 'var(--sidebar-max-width)',
        boxSizing: 'border-box',
        background: T.bg,
        borderRight: T.borderRight,
        padding: mini ? theme.spacing(1) : theme.spacing(2),
        paddingBottom: theme.spacing(4),
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
        overflowY: 'auto',
        transition: theme.transitions.create(['width', 'padding'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        // Medium screens (tablets)
        [theme.breakpoints.between('sm', 'md')]: {
            width: mini ? 'var(--sidebar-collapsed-width)' : '270px',
            padding: theme.spacing(2),
            paddingBottom: theme.spacing(5),
        },
        // Small screens (mobile)
        [theme.breakpoints.down('sm')]: {
            width: '270px',
            padding: theme.spacing(2),
            paddingBottom: theme.spacing(6),
        },
    },
}));

/**
 * Styled version of MUI's ListItem component.
 * Custom styling based on props for active state, chevron buttons, and collapsed sidebar.
 */
const StyledListItem = styled(ListItem, {
    shouldForwardProp: (prop) =>
        !['active', 'isChevron', 'mini', 'indent', 'isProjectItem'].includes(prop),
})(({ theme, active, isChevron, mini, indent }) => ({
    borderRadius: 4,
    marginBottom: theme.spacing(0.25),
    padding: `${theme.spacing(0.875)} ${theme.spacing(1.5)}`,
    paddingLeft: indent ? theme.spacing(3) : theme.spacing(1.5),
    backgroundColor: active ? T.activeBg : 'transparent',
    borderLeft: active ? `3px solid ${T.accent}` : '3px solid transparent',
    justifyContent: isChevron && mini ? 'center' : 'flex-start',
    width: '100%',
    transition: T.transition,
    '&:hover': {
        backgroundColor: active ? T.activeBg : T.hoverBg,
        '& .MuiListItemText-primary': {
            color: T.activeText,
        },
        '& .MuiListItemIcon-root': {
            color: T.activeText,
        },
    },
    '& .MuiListItemText-primary': {
        fontWeight: active ? 600 : 400,
        color: active ? T.activeText : T.inactiveText,
        fontSize: '0.9rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        transition: 'color 160ms ease',
    },
    '& .MuiListItemIcon-root': {
        color: active ? T.activeText : T.inactiveIcon,
        minWidth: isChevron && mini ? 'auto' : 36,
        flexShrink: 0,
        transition: 'color 160ms ease',
    },
}));

/**
 * Floating button used to toggle sidebar in mobile view.
 */
const MenuButton = styled(Box)(({ theme }) => ({
    position: 'fixed',
    top: theme.spacing(1.5),
    left: theme.spacing(2),
    zIndex: theme.zIndex.drawer + 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1),
    borderRadius: '50%',
    backgroundColor: theme.palette.background.paper,
    cursor: 'pointer',
    width: 20,
    height: 20,
}));

/**
 * Section Label for sidebar sections
 */
const SectionLabel = styled(Typography)(({ theme }) => ({
    textTransform: 'uppercase',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: T.sectionLabel,
    paddingLeft: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    letterSpacing: '0.1em',
    [theme.breakpoints.down('sm')]: {
        fontSize: '0.65rem',
        marginTop: theme.spacing(1.5),
        marginBottom: theme.spacing(0.75),
    },
}));

const ProjectChip = styled(Chip)(({ theme }) => ({
    backgroundColor: 'rgba(74, 158, 255, 0.2)',
    color: '#8EC5FF',
    border: '1px solid rgba(74, 158, 255, 0.3)',
    fontWeight: 500,
    fontSize: '0.7rem',
    height: 20,
    maxWidth: 'calc(100% - 8px)',
    marginRight: theme.spacing(1),
    '& .MuiChip-label': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        padding: '0 8px',
    },
}));

/**
 * Sidebar component that dynamically adjusts based on screen size.
 * Uses state for active menu items and shows project-specific navigation
 * when in a project context.
 *
 * @param {Object} props Component props
 * @param {boolean} props.open Whether the sidebar is open
 * @param {Function} props.onClose Function to handle sidebar close
 * @param {string} props.projectId Current project ID (if in project context)
 * @param {string} props.projectName Current project name (if in project context)
 */
const Sidebar = ({ open, onClose: handleSidebarToggle, projectId, projectName = '' }) => {
    const [activeItem, setActiveItem] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const location = useLocation();
    const { canProject, canBusiness, isLoading, logout } = usePermissions();

    // Check if we're currently in a project context
    const isProjectContext = projectId && location.pathname.includes('/projects/');

    // Check if user can see Closeout Docs nav item
    const canSeeCloseoutDocs = !isLoading && canProject(projectId, PERMISSIONS.CLOSEOUT_DOCS_READ);

    // Check if user can see Reports nav item
    const canSeeReports = !isLoading && canProject(projectId, PERMISSIONS.REPORTS_READ);

    // Check if user can see Usage Dashboard nav item (business-level permission)
    const canSeeUsageDashboard = !isLoading && canBusiness(PERMISSIONS.VIEW_USAGE_DASHBOARD);

    // General navigation items (always visible)
    const generalMenuItems = useMemo(() => {
        const items = [
            { id: 'projects', text: 'Projects', icon: <ProjectsIcon />, path: '/projects' },
        ];
        if (canSeeUsageDashboard) {
            items.push({
                id: 'usageDashboard',
                text: 'Usage Dashboard',
                icon: <UsageDashboardIcon />,
                path: '/usage-dashboard',
            });
        }
        return items;
    }, [canSeeUsageDashboard]);

    // Project-specific navigation items (visible only in project context)
    // Filtered based on user permissions
    const projectMenuItems = useMemo(() => {
        const items = [
            {
                id: 'drawings',
                text: 'Drawings',
                icon: <SitePlansIcon />,
                path: `/projects/${projectId}/plans`,
            },
            {
                id: 'deficiencies',
                text: 'Deficiencies',
                icon: <DeficienciesIcon />,
                path: `/projects/${projectId}/deficiencies`,
            },
        ];

        // Only show Reports if user has REPORTS_READ permission
        if (canSeeReports) {
            items.push({
                id: 'reports',
                text: 'Reports',
                icon: <ReportsIcon />,
                path: `/projects/${projectId}/reports`,
            });
        }

        // Only show Closeout Docs if user has CLOSEOUT_DOCS_READ permission
        if (canSeeCloseoutDocs) {
            items.push({
                id: 'closeoutDocs',
                text: 'Closeout Docs',
                icon: <CloseoutDocsIcon />,
                path: `/projects/${projectId}/closeout-docs`,
            });
        }

        return items;
    }, [projectId, canSeeReports, canSeeCloseoutDocs]);

    // Determine active item based on current path
    useEffect(() => {
        // For general pages
        if (location.pathname === '/') {
            setActiveItem('projects');
        } else if (location.pathname === '/projects') {
            setActiveItem('projects');
        } else if (location.pathname === '/usage-dashboard') {
            setActiveItem('usageDashboard');
        }
        // For project-specific pages
        else if (location.pathname.includes('/projects/')) {
            if (location.pathname.includes('/plans')) {
                setActiveItem('drawings');
            } else if (location.pathname.includes('/deficiencies')) {
                setActiveItem('deficiencies');
            } else if (location.pathname.includes('/reports')) {
                setActiveItem('reports');
            } else if (location.pathname.includes('/closeout-doc')) {
                setActiveItem('closeoutDocs');
            } else {
                // Project dashboard
                setActiveItem('projectDashboard');
            }
        } else if (location.pathname === '/deficiencies') {
            setActiveItem('deficiencies');
        }
    }, [location.pathname]);

    /**
     * Handles sidebar item clicks, updating the active item.
     * Closes sidebar on mobile after selection.
     * Navigates to the appropriate route.
     */
    const handleItemClick = (item) => {
        setActiveItem(item.id);
        navigate(item.path);

        if (isMobile) {
            handleSidebarToggle();
        }
    };

    const handleLogout = async () => {
        try {
            await fetch(`${API_URL}/logout`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            });
        } catch (error) {
            console.error('Logout request failed:', error);
        } finally {
            logout();
            navigate('/');
        }
    };

    // Display a shortened project name on mobile if needed
    const displayProjectName = () => {
        if (!projectName) return 'Project Details';

        if (isMobile && projectName.length > 20) {
            return projectName.substring(0, 18) + '...';
        }

        return projectName;
    };

    /**
     * Renders the contents of the sidebar, including menu items and project information.
     */
    const drawerContent = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                minHeight: isMobile ? 'calc(100% - 20px)' : '100%',
                pb: isMobile ? 4 : 2,
            }}
        >
            {/* Main Navigation Section */}
            <List
                sx={{
                    flexGrow: 1,
                    pt: 0,
                    width: '100%',
                    '& .MuiListItemText-root': {
                        minWidth: 0,
                        flex: '1 1 auto',
                    },
                }}
            >
                {/* General navigation items */}
                {!isMobile && !open ? null : <SectionLabel>General</SectionLabel>}
                {generalMenuItems.map((item) => (
                    <StyledListItem
                        button
                        key={item.id}
                        active={activeItem === item.id}
                        onClick={() => handleItemClick(item)}
                    >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        {!isMobile && !open ? null : <ListItemText primary={item.text} />}
                    </StyledListItem>
                ))}

                {/* Project context menu items */}
                {isProjectContext && (
                    <>
                        {!isMobile && !open ? null : (
                            <>
                                <Divider sx={{ my: 1.5, borderColor: T.divider }} />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        px: 2,
                                        mb: 1,
                                        flexWrap: 'wrap',
                                        maxWidth: '100%',
                                    }}
                                >
                                    <SectionLabel sx={{ m: 0, p: 0, flexShrink: 0 }}>
                                        Project
                                    </SectionLabel>
                                    <ProjectChip
                                        label={displayProjectName()}
                                        size="small"
                                        sx={{
                                            ml: 'auto',
                                            maxWidth: {
                                                xs: 'calc(100% - 65px)',
                                                sm: 'calc(100% - 80px)',
                                            },
                                            '.MuiChip-label': {
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                fontSize: isMobile ? '0.65rem' : '0.7rem',
                                                padding: isMobile ? '0 6px' : '0 8px',
                                            },
                                        }}
                                    />
                                </Box>
                            </>
                        )}

                        {/* Project Dashboard item - Always visible when in project context */}
                        <StyledListItem
                            button
                            key="projectDashboard"
                            active={activeItem === 'projectDashboard'}
                            onClick={() => navigate(`/projects/${projectId}`)}
                        >
                            <ListItemIcon>
                                <HomeIcon />
                            </ListItemIcon>
                            {!isMobile && !open ? null : (
                                <ListItemText primary="Project Dashboard" />
                            )}
                        </StyledListItem>

                        {/* Project navigation items */}
                        {projectMenuItems.map((item) => (
                            <StyledListItem
                                button
                                key={item.id}
                                active={activeItem === item.id}
                                onClick={() => handleItemClick(item)}
                                indent={!isMobile && open}
                            >
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                {!isMobile && !open ? null : <ListItemText primary={item.text} />}
                            </StyledListItem>
                        ))}
                    </>
                )}
            </List>

            {/* Bottom section: account actions and a separate sidebar toggle */}
            <Box
                sx={{
                    mt: 'auto',
                    mb: isMobile ? 2 : 0,
                    width: '100%',
                }}
            >
                <Divider sx={{ mb: 1, borderColor: T.divider }} />

                {/* Settings nav item - always visible (Account Settings accessible to all users) */}
                <StyledListItem
                    button
                    active={activeItem === 'settings'}
                    onClick={() => handleItemClick({ id: 'settings', path: '/settings' })}
                    sx={{ mb: 0 }}
                >
                    <ListItemIcon>
                        <SettingsIcon />
                    </ListItemIcon>
                    {!isMobile && !open ? null : <ListItemText primary="Settings" />}
                </StyledListItem>

                <StyledListItem button onClick={handleLogout} aria-label="Log out" sx={{ mb: 0 }}>
                    <ListItemIcon>
                        <LogoutIcon />
                    </ListItemIcon>
                    {!isMobile && !open ? null : <ListItemText primary="Logout" />}
                </StyledListItem>

                {/* Sidebar collapse/expand control (desktop only) */}
                {!isMobile && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: open ? 'flex-end' : 'center',
                            mt: 1,
                            pt: 1,
                            borderTop: `1px solid ${T.divider}`,
                        }}
                    >
                        <Tooltip
                            title={open ? 'Collapse sidebar' : 'Expand sidebar'}
                            placement="right"
                        >
                            <IconButton
                                onClick={handleSidebarToggle}
                                aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
                                size="small"
                                sx={{
                                    width: 36,
                                    height: 36,
                                    color: T.inactiveIcon,
                                    borderRadius: 1,
                                    '&:hover': {
                                        color: T.activeText,
                                        backgroundColor: T.hoverBg,
                                    },
                                }}
                            >
                                {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        </Box>
    );

    return (
        <>
            {/* Mobile menu toggle button */}
            {isMobile && (
                <MenuButton
                    id="sidebar-mobile-toggle"
                    onClick={handleSidebarToggle}
                    sx={{ display: open ? 'none' : 'flex' }}
                >
                    <ChevronRightIcon fontSize="small" />
                </MenuButton>
            )}

            {/* Drawer with sidebar content */}
            <StyledDrawer
                className={open ? 'Sidebar' : 'Sidebar collapsed'}
                variant={isMobile ? 'temporary' : 'permanent'}
                open={open}
                onClose={handleSidebarToggle}
                mini={!open}
                ModalProps={{
                    keepMounted: true,
                }}
            >
                {drawerContent}
            </StyledDrawer>
        </>
    );
};

export default Sidebar;
